import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Sun, Moon, ChevronDown } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { LANGS, CATEGORY_KEYS } from "@/i18n";
import { usePages } from "@/contexts/PagesContext";

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const { pages } = usePages();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  const catLink = (c) => `/collectie/${c}`;
  const navPages = (pages || []).filter((p) => p.show_in_nav);
  const pageLabel = (p) => p.titles?.[lang] || p.titles?.de || p.slug;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 vl-glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" className="text-left" data-testid="logo-btn">
          <img src="/veloura-logo.png" alt="Veloura" className="vl-logo h-11 sm:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <Link to="/" className="text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="nav-home">{t("nav_home")}</Link>
          <div className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
            <Link to="/collecties" className="flex items-center gap-1 text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="nav-collections">
              {t("nav_collections")} <ChevronDown size={14} />
            </Link>
            {dropOpen && (
              <div className="absolute top-full left-0 pt-3" data-testid="collections-dropdown">
                <div className="vl-glass rounded-xl border border-[var(--vl-border)] py-2 w-56 shadow-xl">
                  {CATEGORY_KEYS.map((c) => (
                    <Link key={c} to={catLink(c)} className="block px-5 py-2.5 text-sm text-[var(--vl-stone)] hover:text-[var(--vl-gold)] hover:bg-[var(--vl-obsidian)] transition-colors" data-testid={`dropdown-${c}`}>
                      {t(`cat_${c}`)}
                    </Link>
                  ))}
                  <Link to="/collectie/accessoires" className="block px-5 py-2.5 text-sm text-[var(--vl-stone)] hover:text-[var(--vl-gold)] hover:bg-[var(--vl-obsidian)] transition-colors" data-testid="dropdown-accessoires">
                    {t("nav_accessories")}
                  </Link>
                </div>
              </div>
            )}
          </div>
          <Link to="/blog" className="text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="nav-blog">{t("nav_blog")}</Link>
          <Link to="/over-ons" className="text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="nav-about">{t("nav_about")}</Link>
          <Link to="/contact" className="text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="nav-contact">{t("nav_contact")}</Link>
          {navPages.map((p) => (
            <Link key={p.id} to={`/${p.slug}`} className="text-sm tracking-widest uppercase text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid={`nav-page-${p.slug}`}>{pageLabel(p)}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:flex items-center gap-1 text-[var(--vl-stone)]">
            <Globe size={15} className="text-[var(--vl-gold)]" />
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)}
                className={`px-1.5 text-xs tracking-wider uppercase transition-colors ${lang === l.code ? "text-[var(--vl-gold)]" : "hover:text-[var(--vl-ivory)]"}`} data-testid={`lang-${l.code}`}>
                {l.code}
              </button>
            ))}
          </div>
          <button onClick={toggle} className="text-[var(--vl-gold)] hover:text-[var(--vl-gold-bright)] transition-colors" data-testid="theme-toggle" aria-label="theme">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Link to="/termin" className="hidden sm:inline-block vl-btn-gold rounded-full px-5 py-2.5 text-xs tracking-widest uppercase font-medium" data-testid="header-appointment-btn">
            {t("nav_appointment")}
          </Link>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-[var(--vl-gold)]" data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden vl-glass border-t border-[var(--vl-border)] px-6 py-5 space-y-3" data-testid="mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_home")}</Link>
          <Link to="/collecties" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_collections")}</Link>
          {CATEGORY_KEYS.map((c) => (
            <Link key={c} to={catLink(c)} onClick={() => setOpen(false)} className="block pl-4 text-sm text-[var(--vl-stone)]">— {t(`cat_${c}`)}</Link>
          ))}
          <Link to="/collectie/accessoires" onClick={() => setOpen(false)} className="block pl-4 text-sm text-[var(--vl-stone)]">— {t("nav_accessories")}</Link>
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_blog")}</Link>
          <Link to="/over-ons" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_about")}</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_contact")}</Link>
          {navPages.map((p) => (
            <Link key={p.id} to={`/${p.slug}`} onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]" data-testid={`mobile-nav-page-${p.slug}`}>{pageLabel(p)}</Link>
          ))}
          <Link to="/meine-termine" onClick={() => setOpen(false)} className="block text-sm tracking-widest uppercase text-[var(--vl-stone)]">{t("nav_manage")}</Link>
          <Link to="/termin" onClick={() => setOpen(false)} className="block vl-btn-gold rounded-full px-5 py-3 text-center text-xs tracking-widest uppercase font-medium mt-2">{t("nav_appointment")}</Link>
          <div className="flex items-center gap-3 pt-2">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} className={`text-xs tracking-wider uppercase ${lang === l.code ? "text-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`}>{l.label}</button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
